'use client';

import { JSX, useEffect, useRef, useState } from 'react';

// components
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';

// types
import { EIcons } from '@/components/atoms/at-icon/icons';
import { sendGTMClickEvent } from '@/components/helpers/analytics/gtm';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';

export interface IMlInfoTooltipContentProps {
  body: string;
  floatingRef: (node: HTMLDivElement | null) => void;
  floatingStyle: React.CSSProperties;
  id: string;
  shouldAutoFocus: boolean;
  tooltipName?: string;
  onClose: () => void;
}

export const MlInfoTooltipContent = ({
  body,
  floatingRef,
  floatingStyle,
  id,
  shouldAutoFocus,
  tooltipName,
  onClose,
}: IMlInfoTooltipContentProps): JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasClosed, setHasClosed] = useState(false);

  useEffect(() => {
    if (shouldAutoFocus) {
      const raf = requestAnimationFrame(() => {
        contentRef.current?.focus();
      });

      return () => cancelAnimationFrame(raf);
    }
  }, [shouldAutoFocus]);

  const setRefs = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (typeof floatingRef === 'function') {
      floatingRef(node);
    }
  };

  const handleOnClose = () => {
    sendGTMClickEvent('close', 'ml-info-tooltip-content', 'not_available');
    setHasClosed(true);
    onClose();
  };

  return (
    <div className="relative z-10">
      {hasClosed && (
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {tooltipName
            ? `The ${tooltipName} tooltip is closed.`
            : 'The tooltip is closed.'}
        </span>
      )}
      <div
        id={id}
        role="tooltip"
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-body`}
        tabIndex={-1}
        ref={setRefs}
        style={floatingStyle}
        className="w-280 rounded-30 bg-skin-primary p-6 pl-8 shadow-card focus:outline-none focus-visible:outline-2 focus-visible:outline-black md:w-380"
      >
        <div id={`${id}-label`} className="sr-only">
          {tooltipName
            ? `The ${tooltipName} tooltip now has focus.`
            : 'This tooltip now has focus.'}
        </div>

        <div className="mb-4 flex justify-between">
          <AtIcon
            aria-hidden="true"
            className="text-20"
            icon={EIcons.INFO_FILL}
            color="var(--color-ui-icon-primary"
          />
        </div>

        <div id={`${id}-body`} className="text-balance">
          <RenderRichText body={body} />
        </div>
        <button
          className="absolute right-4 top-5"
          aria-label="Close tooltip"
          onClick={handleOnClose}
        >
          <AtIcon
            className="text-24"
            aria-hidden="true"
            icon={EIcons.CLOSE}
            color="var(--color-ui-icon-primary)"
          />
        </button>
      </div>
    </div>
  );
};
